import { CellOfRow } from '@components/tables';
import { EnableStatus, NASMountType, NASStatus } from '@EcamModel/model/PMC8266StorageSetting';
import { IntervalUnit, PMC8266NotifyConfig } from '@EcamModel/model/PMC826SnapshotSetting';
import { Button, Stack, TableBody, TableRow, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { SectionsCollapsePMC8266 } from '@pages/cameras/hook/useMilesightPMC8266';
import color from '@theme/Colors';
import _ from 'lodash';
import { useState } from 'react';
import { BiHide, BiShow } from 'react-icons/bi';
import { transformScheduleData } from 'src/helpers';
import {
    BaseSectionCollapse,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
} from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';

const nasMountTypeMapping: { [key in NASMountType]: string } = {
    [NASMountType.NFS]: 'NFS',
    [NASMountType.SMBCIFS]: 'SMB/CIFS',
};

const nasStatusMapping: { [key in NASStatus]: string } = {
    [NASStatus.Unformatted]: 'Unformatted',
    [NASStatus.Normal]: 'Normal',
    [NASStatus.Offline]: 'Offline',
    [NASStatus.Formatting]: 'Formatting',
};

const enableStatusMapping: { [key in EnableStatus]: string } = {
    [EnableStatus.Disable]: 'Disable',
    [EnableStatus.Enable]: 'Enable',
};

const intervalUnitMapping: { [key in IntervalUnit]: string } = {
    [IntervalUnit.Millisecond]: 'ms',
    [IntervalUnit.Second]: 's',
    [IntervalUnit.Minute]: 'm',
    [IntervalUnit.Hour]: 'h',
    [IntervalUnit.Day]: 'd',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePMC8266]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePMC8266) => void;
};

const tableHeaders: string[] = [
    'No.',
    'Server address',
    'Directory',
    'Mounting type',
    'Total',
    'Free',
    'User name',
    'Status',
];

export default function StoragePMC8266(props: Props) {
    const { camera } = useCameraDetailContext();

    const [buttonSelected, setButtonSelected] = useState<number>(1);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const configs = camera?.PMC8266Camera?.PMC8266CameraConfigs;

    const PMC8266Storage = configs?.StorageSetting;
    const PMC8266Record = configs?.RecordSetting;
    const PMC8266SnapShot = configs?.SnapshotSetting;

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const handleButtonClick = (index: number) => {
        setButtonSelected(index);
    };

    const selectedSnapshot = PMC8266SnapShot?.NotifyList?.[buttonSelected - 1];

    const snapshotInfo = {
        Url: selectedSnapshot?.Url || '',
        Enable: selectedSnapshot?.Enable,
        User: selectedSnapshot?.User || '',
        Password: selectedSnapshot?.Password || '',
    } as PMC8266NotifyConfig;

    const sectionsStorageManagement: SectionHasSubItemProps[] = [
        {
            title: 'SD card',
            data: [
                {
                    label: 'SD card:',
                    value: (
                        <Typography>{`${PMC8266Storage?.SdcardFreeSize!}M / ${PMC8266Storage?.SdcardUseSize!}M`}</Typography>
                    ),
                },
            ],
        },
        {
            title: 'NAS',
            tableHeaders: tableHeaders,
            items: PMC8266Storage?.NasList,
            tableBody: (
                <TableBody>
                    {PMC8266Storage?.NasList?.map((row, index) => {
                        return (
                            <TableRow key={index}>
                                <CellOfRow value={`${index + 1}`} align="left" />
                                <CellOfRow value={row.NasAddress} align="left" />
                                <CellOfRow value={row.NasFilePath} align="left" />
                                <CellOfRow value={nasMountTypeMapping[row.NasMountType]} align="left" />
                                <CellOfRow value={row.NasTotalSize} align="left" />
                                <CellOfRow value={row.NasFreeSize} align="left" />
                                <CellOfRow value={row.NasUserName} align="left" />
                                <CellOfRow value={nasStatusMapping[row.NasStatus]} align="left" />
                            </TableRow>
                        );
                    })}
                </TableBody>
            ),
        },
    ];

    const sectionsRecordSettings: SectionHasSubItemProps[] = [
        {
            title: 'Storage settings',
            data: [
                {
                    label: 'Enable recycle storage:',
                    value: <Typography>{enableStatusMapping[PMC8266Record?.RecycleStorageEnable!]}</Typography>,
                },
                {
                    label: 'Pre-record:',
                    value: <Typography>{`${PMC8266Record?.PreRecordTime!}s`}</Typography>,
                },
            ],
        },
        {
            title: 'Schedule settings',
            data: transformScheduleData(PMC8266Record?.ScheduleList!, (dayName, schedule) => ({
                label: `${dayName}:`,
                value: schedule,
            })),
        },
    ];

    const sectionsSnapshotSettings: SectionHasSubItemProps[] = [
        {
            title: 'Snapshot settings',
            data: [
                {
                    label: 'Enable timing snapshot:',
                    value: <Typography>{enableStatusMapping[PMC8266SnapShot?.TimingSnapshotEnable!]}</Typography>,
                },
                {
                    label: 'Interval:',
                    value: (
                        <Typography>{`${PMC8266SnapShot?.IntervalValue}${
                            intervalUnitMapping[PMC8266SnapShot?.IntervalUnit!]
                        }`}</Typography>
                    ),
                },
                {
                    label: 'Save to storage:',
                    value: (
                        <Typography>{`${enableStatusMapping[PMC8266SnapShot?.SaveIntoStorage!]} ${
                            PMC8266SnapShot?.SaveIntoStorage === EnableStatus.Disable
                                ? '(Please mount storage device.)'
                                : ''
                        }`}</Typography>
                    ),
                },
                {
                    label: 'Upload via FTP:',
                    value: (
                        <Typography>{`${enableStatusMapping[PMC8266SnapShot?.UploadViaFtp!]} ${
                            PMC8266SnapShot?.UploadViaFtp === EnableStatus.Disable ? '(Please enable the FTP.)' : ''
                        }`}</Typography>
                    ),
                },
                {
                    label: 'Upload via Email:',
                    value: (
                        <Typography>{`${enableStatusMapping[PMC8266SnapShot?.UploadViaSmtp!]} ${
                            PMC8266SnapShot?.UploadViaSmtp === EnableStatus.Disable ? '(Please enable the Email.)' : ''
                        }`}</Typography>
                    ),
                },
                {
                    label: 'HTTP post:',
                    value: <Typography>{enableStatusMapping[PMC8266SnapShot?.UploadViaHttp!]}</Typography>,
                },
                ...(PMC8266SnapShot?.UploadViaHttp === EnableStatus.Enable
                    ? [
                          {
                              label: 'URL:',
                              value: (
                                  <Stack direction={'row'} spacing={2}>
                                      {PMC8266SnapShot?.NotifyList?.map((value) => (
                                          <Button
                                              key={value.Index}
                                              variant="contained"
                                              size="small"
                                              onClick={() => handleButtonClick(value.Index + 1)}
                                              sx={{
                                                  backgroundColor: 'transparent',
                                                  border:
                                                      buttonSelected === value.Index + 1
                                                          ? `1px solid ${color.success}`
                                                          : `1px solid ${color.grey200}`,
                                                  minWidth: '50px',
                                                  '&:hover': {
                                                      backgroundColor:
                                                          buttonSelected === value.Index + 1
                                                              ? '#E5F5ED'
                                                              : color.grey200,
                                                      borderColor:
                                                          buttonSelected === value.Index + 1
                                                              ? color.success
                                                              : color.grey200,
                                                  },
                                              }}
                                          >
                                              <Typography textAlign={'center'} fontSize={12}>
                                                  {value.Index + 1}
                                              </Typography>
                                          </Button>
                                      ))}
                                  </Stack>
                              ),
                          },
                          {
                              label: '',
                              value: <Typography>{snapshotInfo.Url}</Typography>,
                          },
                          {
                              label: 'Enable:',
                              value: <Typography>{enableStatusMapping[snapshotInfo.Enable]}</Typography>,
                          },
                          {
                              label: 'User name:',
                              value: <Typography>{snapshotInfo.User}</Typography>,
                          },
                          {
                              label: 'Password:',
                              value: (
                                  <Stack
                                      direction={'row'}
                                      justifyContent={'space-between'}
                                      width={'100%'}
                                      alignItems={'center'}
                                  >
                                      <Typography>
                                          {showPassword
                                              ? snapshotInfo.Password
                                              : snapshotInfo.Password?.replace(/./g, '*')}
                                      </Typography>

                                      {_.isNil(snapshotInfo.Password) && (
                                          <>
                                              {showPassword ? (
                                                  <BiHide
                                                      fontSize={18}
                                                      color="#85858A"
                                                      cursor="pointer"
                                                      onClick={() => setShowPassword(false)}
                                                  />
                                              ) : (
                                                  <BiShow
                                                      fontSize={18}
                                                      color="#85858A"
                                                      cursor="pointer"
                                                      onClick={() => setShowPassword(true)}
                                                  />
                                              )}
                                          </>
                                      )}
                                  </Stack>
                              ),
                          },
                      ]
                    : []),
            ],
        },
        {
            title: 'Schedule settings',
            data: transformScheduleData(PMC8266SnapShot?.ScheduleList!, (dayName, schedule) => ({
                label: `${dayName}:`,
                value: schedule,
            })),
        },
    ];

    return (
        <Stack id="storage_management" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            <SectionHasSubItemHeader id="" title="Storage management" onEdit={handleEdit} />

            <BaseSectionCollapse
                title="Storage management"
                isOpen={props.isOpenCollapse.storage_management}
                onToggle={() => {
                    props.handleToggleCollapse(SectionsCollapsePMC8266.StorageManagement);
                }}
                sections={sectionsStorageManagement}
            />

            <BaseSectionCollapse
                title="Record settings"
                id="record_settings"
                isOpen={props.isOpenCollapse.record_settings}
                onToggle={() => {
                    props.handleToggleCollapse(SectionsCollapsePMC8266.RecordSettings);
                }}
                sections={sectionsRecordSettings}
            />

            <BaseSectionCollapse
                title="Snapshot settings"
                id="snapshot_settings"
                isOpen={props.isOpenCollapse.snapshot_settings}
                onToggle={() => {
                    props.handleToggleCollapse(SectionsCollapsePMC8266.SnapshotSettings);
                }}
                sections={sectionsSnapshotSettings}
            />
        </Stack>
    );
}
